import { CommandProps, Mark, markInputRule, markPasteRule, mergeAttributes, RawCommands } from "@tiptap/core";
import { Mark as PMMark } from "@tiptap/pm/model";
import { v4 as uuid } from "uuid";

import { HighlightColors } from "@/components/nw-highlight/models";
import { getSelection } from "@/editor/utils/selection";

export * from "./interactions";

/**
 * Interface for highlight attributes
 */
interface HighlightAttributes {
  group: string;
  color: string;
  active?: boolean;
}

declare module "@tiptap/core" {
  interface Commands<ReturnType> {
    highlight: {
      /**
       * Set a highlight mark
       * @param attributes The highlight attributes
       * @example editor.commands.setHighlight({ color: 'red' })
       */
      setHighlight: (attributes?: { color: HighlightColors }) => ReturnType;
      /**
       * Toggle a highlight mark
       * @param attributes The highlight attributes
       * @example editor.commands.toggleHighlight({ color: 'red' })
       */
      toggleHighlight: (attributes?: { color: HighlightColors }) => ReturnType;
      /**
       * Unset a highlight mark
       * @example editor.commands.unsetHighlight()
       */
      unsetHighlight: (groupId: string) => ReturnType;
      /**
       * Change the color of a highlight mark
       * @param groupId The group ID of the highlight
       * @param color The color of the highlight
       * @example editor.commands.changeHighlightColor('group1', 'red')
       */
      changeHighlightColor: (groupId: string, color: HighlightColors) => ReturnType;
    };
  }
}

export interface HighlightOptions {
  /**
   * HTML attributes to add to the highlight element.
   * @default {}
   * @example { class: "foo" }
   */
  HTMLAttributes: Record<string, unknown>;
}

/**
 * Matches a highlight to a ==highlight== on input.
 */
export const inputRegex = /(?:^|\s)(==(?!\s+==)((?:[^=]+))==(?!\s+==))$/;

/**
 * Matches a highlight to a ==highlight== on paste.
 */
export const pasteRegex = /(?:^|\s)(==(?!\s+==)((?:[^=]+))==(?!\s+==))/g;

export const HighlightExtension = Mark.create<HighlightOptions>({
  name: "highlight",
  group: "inline",
  priority: 1000,
  inclusive: true,

  addAttributes() {
    return {
      group: {
        default: null,
        parseHTML: element => element.getAttribute("group"),
        renderHTML: attributes => {
          if (!attributes.group) {
            attributes.group = `${uuid()}`;
          }
          return {
            group: attributes.group,
          };
        },
      },
      color: {
        parseHTML: (element: HTMLElement) => element.getAttribute("color"),
        renderHTML: (attributes: HighlightAttributes) => {
          if (!attributes.color) {
            return undefined;
          }

          return {
            color: attributes.color,
          };
        },
      },
    };
  },

  parseHTML() {
    return [
      {
        tag: "nw-highlight",
      },
    ];
  },

  renderHTML({ HTMLAttributes }) {
    return ["nw-highlight", mergeAttributes(this.options.HTMLAttributes, HTMLAttributes), ["span", 0]];
  },

  addCommands() {
    return {
      changeHighlightColor:
        (groupId: string, color: HighlightColors) =>
        ({ state, dispatch }: CommandProps): PMMark | boolean => {
          const { tr } = state;
          const { doc } = state;
          let hasChanged = false;

          doc.descendants((node, pos) => {
            const existingMarks = node.marks.filter(
              mark => mark.type.name === "highlight" && mark.attrs.group === groupId,
            );

            if (existingMarks.length > 0) {
              const newHighlightMark = state.schema.marks.highlight.create({
                ...existingMarks[0].attrs,
                color: color,
              });

              tr.removeMark(pos, pos + node.nodeSize, this.type);
              tr.addMark(pos, pos + node.nodeSize, newHighlightMark);
              hasChanged = true;
            }
          });

          if (hasChanged) {
            dispatch?.(tr);
            return true;
          }

          return false;
        },

      setHighlight:
        attributes =>
        ({ tr, state, dispatch, editor, view }: CommandProps): PMMark | boolean => {
          const selection = getSelection(editor, view);

          if (selection == null) {
            return false;
          }

          const { $from, $to } = selection;
          const mark = this.type.create({ ...attributes, active: true });

          // Check if the mark already exists in the selection
          const exists = state.doc.rangeHasMark($from.pos, $to.pos, this.type);

          if (exists) {
            // remove all existing marks
            tr.removeMark($from.pos, $to.pos, this.type);
          }

          tr.addMark($from.pos, $to.pos, mark);
          dispatch?.(tr);
          return mark;
        },
      toggleHighlight:
        attributes =>
        ({ tr, state, dispatch, editor, view }: CommandProps): PMMark | boolean => {
          const selection = getSelection(editor, view);

          if (selection == null) {
            return false;
          }

          const mark = this.type.create(attributes);
          const { $from, $to } = selection;

          // Check if the mark already exists in the selection
          const exists = state.doc.rangeHasMark($from.pos, $to.pos, this.type);

          if (exists) {
            // If the mark exists, remove it
            tr.removeMark($from.pos, $to.pos, this.type);
            dispatch?.(tr);
            return false;
          }

          // If the mark doesn't exist, add it
          tr.addMark($from.pos, $to.pos, mark);
          dispatch?.(tr);
          return mark;
        },
      unsetHighlight:
        (groupId: string) =>
        ({ tr, state, dispatch }: CommandProps): boolean => {
          state.doc.descendants((node, pos) => {
            node.marks.forEach(mark => {
              if (mark.type.name === this.name && mark.attrs.group === groupId) {
                const from = pos;
                const to = pos + node.nodeSize;
                tr.removeMark(from, to, mark.type);
              }
            });
          });

          if (tr.docChanged) {
            dispatch?.(tr);
            return true;
          }

          return false;
        },
    } as Partial<RawCommands>;
  },

  addKeyboardShortcuts() {
    return {
      "Mod-Shift-h": () => !!this.editor.commands.toggleHighlight({ color: HighlightColors.YELLOW }),
    };
  },

  addInputRules() {
    return [
      markInputRule({
        find: inputRegex,
        type: this.type,
      }),
    ];
  },

  addPasteRules() {
    return [
      markPasteRule({
        find: pasteRegex,
        type: this.type,
      }),
    ];
  },
});
